/**
 * FormUdfRenderer — dynamic UDF fields via vision matrix layout.
 */
import React, { useEffect, useMemo, useReducer } from 'react';
import styled from 'styled-components';
import type { Control, FieldValues } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import {
  solveMatrix,
  BREAKPOINTS,
  DEFAULT_MATRIX_COLUMN_GAP_PX,
  type Breakpoint,
  type CompiledCell,
} from '@packages/matrix-solver';
import { rem } from '@packages/ui/tokens';
import { RhfCheckbox, RhfDatePicker, RhfInputNumber, RhfInputText, RhfSelect } from '@packages/ui/hook-form-adapters';
import { Lozenge } from '@packages/ui/primitives/Lozenge';
import { FormField } from '@packages/ui/patterns/FormField';
import { Typography } from '@packages/ui/primitives';
import { ViewField } from '@packages/ui/patterns/ViewField';
import type { ViewPresentation } from '../../types/schema';
import type { FormUdfNode } from '../../types/layout';
import { useFormEngineForm, useFormEngineState } from '../../context/FormEngineContext';
import { useMatrixContainerWidth } from '../../hooks/useMatrixContainerWidth';
import { MatrixFieldsRoot, MatrixFieldShell, compileCellPlacement } from './MatrixFieldsMesh';
import { ActionRegistry } from '@packages/table-builder/registry';

/**
 * UdfSectionRoot wraps the entire UDF block (title + fields grid).
 * It sits outside the static MatrixFieldsRoot so it does not participate in
 * the static-fields row-gap. Instead it uses the same top margin as a
 * non-first MatrixSectionDivider so the rhythm matches static sections:
 *   margin-top: 16px  (same as SectionDividerRoot $isFirst=false)
 */
const UdfSectionRoot = styled.div<{ $compact?: boolean }>`
  width: 100%;
  margin-top: ${({ $compact }) => ($compact ? rem(8) : rem(24))};
`;

/**
 * UDF section heading — identical styles to MatrixFieldsMesh DividerHeading
 * so "Other Information" looks the same as "Participant Detail" etc.
 *
 * The heading sits OUTSIDE the nested MatrixFieldsRoot so no row-gap is
 * added between it and the field grid. margin-bottom: 12px directly sets
 * the heading-to-first-field gap, matching legacy `.employeeAddress`
 * padding-bottom of 12px.
 */
const UdfSectionHeading = styled.div`
  font-family: var(--ui-font-heading, var(--ui-font-sans, 'HK Grotesk', sans-serif));
  font-size: ${rem(20)};
  font-weight: 400;
  line-height: ${rem(24)};
  color: #595959;
  margin-bottom: ${rem(12)};
`;

interface UdfFieldDef {
  id: string;
  fieldName: string;
  fieldType: string;
  isMandatory?: boolean;
  options?: Array<{ optionValue?: string; label?: string; value?: string }>;
}

/**
 * In-flight dedup map — prevents duplicate concurrent requests for the same
 * actionId:moduleId pair while a request is already in progress.
 * Cleared as soon as the request settles (no persistent cache).
 */
const udfInflight = new Map<string, Promise<UdfFieldDef[]>>();

function dedupKey(actionId: string, moduleId: number): string {
  return `${actionId}:${moduleId}`;
}

function getUdfFields(actionId: string, moduleId: number): Promise<UdfFieldDef[]> {
  const key = dedupKey(actionId, moduleId);
  const inflight = udfInflight.get(key);
  if (inflight) return inflight;

  const promise = ActionRegistry.executeData(actionId, { moduleId: String(moduleId) })
    .then((result) => {
      udfInflight.delete(key);
      const raw = result as Record<string, unknown>;
      return (raw.fields as UdfFieldDef[]) ?? (raw.data as UdfFieldDef[]) ?? (Array.isArray(result) ? result as UdfFieldDef[] : []);
    })
    .catch((err: unknown) => {
      console.error('[FormUdfRenderer] executeData failed:', err);
      udfInflight.delete(key);
      return [] as UdfFieldDef[];
    });

  udfInflight.set(key, promise);
  return promise;
}

interface UdfHookState {
  fields: UdfFieldDef[];
  loading: boolean;
}

type UdfHookAction = { type: 'LOADED'; fields: UdfFieldDef[] };

function udfHookReducer(state: UdfHookState, action: UdfHookAction): UdfHookState {
  switch (action.type) {
    case 'LOADED':
      return { fields: action.fields, loading: false };
  }
}

function useUdfFields(actionId: string, moduleId: number): UdfHookState {
  const [state, dispatch] = useReducer(udfHookReducer, {
    fields: [],
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    getUdfFields(actionId, moduleId).then((result) => {
      if (cancelled) return;
      dispatch({ type: 'LOADED', fields: result });
    });
    return () => {
      cancelled = true;
    };
  }, [actionId, moduleId]);

  return state;
}

function toFieldKey(fieldName: string): string {
  const clean = fieldName.replaceAll(' ', '');
  return clean.charAt(0).toLowerCase() + clean.slice(1);
}

export type UdfFormItem = { name: string; value: string; moduleId: number };

/**
 * Keep form `userDefinedFields` as the same API array shape
 * `[{ name, value, moduleId }]`. Merge defs + existing values by `name`
 * so create seeds empties and edit preserves fetched values — no flatten.
 */
function useSeedUdfArray(
  form: {
    getValues: (name?: string) => unknown;
    setValue: (name: string, value: unknown, opts?: object) => void;
  },
  prefix: string,
  moduleId: number,
  fieldKeys: Array<{ fieldName: string }>,
): void {
  useEffect(() => {
    if (fieldKeys.length === 0) return;

    const current = form.getValues(prefix);
    const existing = Array.isArray(current)
      ? (current as Array<{ name?: string; value?: unknown; moduleId?: number }>)
      : [];
    const byName = new Map(
      existing
        .filter((item) => typeof item?.name === 'string' && item.name.length > 0)
        .map((item) => [item.name as string, item]),
    );

    const next: UdfFormItem[] = fieldKeys.map((field) => {
      const prev = byName.get(field.fieldName);
      const raw = prev?.value;
      return {
        name: field.fieldName,
        value: raw == null ? '' : String(raw),
        moduleId: typeof prev?.moduleId === 'number' ? prev.moduleId : moduleId,
      };
    });

    const unchanged =
      Array.isArray(current) &&
      current.length === next.length &&
      next.every((item, i) => {
        const cur = (current as UdfFormItem[])[i];
        return (
          cur != null &&
          cur.name === item.name &&
          cur.value === item.value &&
          cur.moduleId === item.moduleId
        );
      });
    if (unchanged) return;

    form.setValue(prefix, next, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [fieldKeys, form, moduleId, prefix]);
}

function normalizeUdfFieldType(fieldType: string): string {
  return fieldType.trim().toLowerCase();
}

/**
 * Read-only UDF field — renders ViewField for `view-fields` presentation.
 * Reads the current value via useWatch and displays it as a formatted label-value pair.
 */
function UdfFieldViewControl({
  field,
  name,
  control,
  viewPresentation,
}: {
  field: UdfFieldDef & { key: string };
  name: string;
  control: Control<FieldValues>;
  viewPresentation?: ViewPresentation;
}): React.ReactElement {
  const value = useWatch({ control, name });
  const displayValue = value != null && value !== '' ? String(value) : '-';

  if (viewPresentation === 'view-fields') {
    return (
      <ViewField
        def={{ label: field.fieldName, schema: {} as any, adapterHint: 'text' } as any}
        value={displayValue}
        overrideLabel={field.fieldName}
      />
    );
  }

  return (
    <FormField label={field.fieldName} semanticId={name} required={false}>
      <Typography variant='body'>{displayValue}</Typography>
    </FormField>
  );
}

interface UdfFieldControlProps {
  field: UdfFieldDef & { key: string };
  name: string;
  control: Control<FieldValues>;
  disabled?: boolean;
}

function UdfFieldControl({ field, name, control, disabled }: UdfFieldControlProps): React.ReactElement {
  const fieldType = normalizeUdfFieldType(field.fieldType);

  if (fieldType === 'dropdown' && field.options) {
    const opts = field.options.map((opt) => ({
      label: opt.optionValue ?? opt.label ?? '',
      value: opt.optionValue ?? opt.value ?? '',
    }));
    return (
      <RhfSelect
        control={control}
        name={name}
        label={field.fieldName}
        required={field.isMandatory}
        disabled={disabled}
        options={opts}
      />
    );
  }

  if (fieldType === 'date') {
    return (
      <RhfDatePicker
        control={control}
        name={name}
        label={field.fieldName}
        required={field.isMandatory}
        disabled={disabled}
      />
    );
  }

  if (fieldType === 'checkbox') {
    return (
      <RhfCheckbox
        control={control}
        name={name}
        label={field.fieldName}
        required={field.isMandatory}
        disabled={disabled}
      />
    );
  }

  if (fieldType === 'number') {
    return (
      <RhfInputNumber
        control={control}
        name={name}
        label={field.fieldName}
        required={field.isMandatory}
        disabled={disabled}
      />
    );
  }

  return (
    <RhfInputText
      control={control}
      name={name}
      label={field.fieldName}
      required={field.isMandatory}
      disabled={disabled}
    />
  );
}

export { useUdfFields, toFieldKey, UdfFieldControl, UdfFieldViewControl, useSeedUdfArray };
export type { UdfFieldDef };

interface FormUdfRendererProps {
  node: FormUdfNode;
  namePrefix?: string;
}

export const FormUdfRenderer = React.memo(function FormUdfRenderer({
  node,
  namePrefix,
}: FormUdfRendererProps): React.ReactElement | null {
  const { form, udfRequiredFieldsRef } = useFormEngineForm();
  const { isReadOnly, viewPresentation } = useFormEngineState();
  const useViewMode = isReadOnly && viewPresentation !== 'disabled-inputs';

  const { containerRef, containerWidth } = useMatrixContainerWidth();
  const title = node.title ?? 'Other Information';
  const basePrefix = node.fieldPrefix ?? 'userDefinedFields';
  const prefix = namePrefix ? `${namePrefix}.${basePrefix}` : basePrefix;

  const { fields, loading } = useUdfFields(node.actionId, node.moduleId);

  const fieldKeys = useMemo(
    () => fields.map((f) => ({ ...f, key: toFieldKey(f.fieldName) })),
    [fields],
  );

  useSeedUdfArray(form, prefix, node.moduleId, fieldKeys);

  useEffect(() => {
    if (useViewMode) return;
    const registry = udfRequiredFieldsRef.current;
    const registeredPaths: string[] = [];

    fieldKeys.forEach((field, idx) => {
      if (field.isMandatory) {
        const path = `${prefix}.${idx}.value`;
        registry.set(path, 'Required field');
        registeredPaths.push(path);
      }
    });

    return () => {
      for (const path of registeredPaths) {
        registry.delete(path);
      }
    };
  }, [fieldKeys, prefix, udfRequiredFieldsRef, useViewMode]);

  const solvedMatrix = useMemo(() => {
    if (fieldKeys.length === 0) return null;

    const fieldMinWidthPx = node.fieldMinWidthPx ?? 150;
    const fieldMaxWidthPx = node.fieldMaxWidthPx ?? 250;
    const matrixFields = fieldKeys.map((field, idx) => ({
      id: field.key,
      order: idx,
      tolerance: {
        minCols: 1,
        maxCols: 1,
        minWidthPx: fieldMinWidthPx,
        maxWidthPx: fieldMaxWidthPx,
      },
      visible: Object.fromEntries(BREAKPOINTS.map((bp) => [bp, true])) as Partial<
        Record<Breakpoint, boolean>
      >,
      kind: 'field' as const,
    }));

    const visibility = Object.fromEntries(
      BREAKPOINTS.map((bp) => [
        bp,
        Object.fromEntries(matrixFields.map((f) => [f.id, true])),
      ]),
    ) as Partial<Record<Breakpoint, Record<string, boolean>>>;

    return solveMatrix({
      row: {
        id: 'udf_fields_mesh',
        fields: matrixFields,
        columns: node.columns,
      },
      visibility,
      options: {
        preserveHiddenFootprint: true,
        responsiveDownshift: true,
        containerWidth,
        columnGapPx: DEFAULT_MATRIX_COLUMN_GAP_PX,
        log: import.meta.env.DEV ? console.debug : undefined,
      },
    });
  }, [fieldKeys, node.columns, containerWidth, node.fieldMinWidthPx, node.fieldMaxWidthPx]);

  const columnCount = solvedMatrix?.base.columnCount ?? 1;

  const cellMap = useMemo(() => {
    if (!solvedMatrix) return {};
    const map: Record<string, Partial<Record<Breakpoint, CompiledCell>>> = {};
    BREAKPOINTS.forEach((bp) => {
      solvedMatrix[bp].cells.forEach((cell) => {
        if (!map[cell.id]) map[cell.id] = {};
        map[cell.id][bp] = cell;
      });
    });
    return map;
  }, [solvedMatrix]);

  if (loading || fieldKeys.length === 0 || !solvedMatrix) return null;

  const control = form.control as unknown as Control<FieldValues>;

  const titleNode = title != null && title !== '' ? (
    node.titleStyle === 'lozenge'
      ? <Lozenge variant='primary' size='md' className='mb-0'>{title}</Lozenge>
      : <UdfSectionHeading>{title}</UdfSectionHeading>
  ) : null;

  return (
    <UdfSectionRoot $compact={node.titleStyle === 'lozenge'}>
      {titleNode}
      <MatrixFieldsRoot ref={containerRef} columnCount={columnCount}>
        {fieldKeys.map((field, idx) => {
          const name = `${prefix}.${idx}.value`;
          const bpCells = cellMap[field.key];
          if (!bpCells) return null;

          const placementCSS = compileCellPlacement(cellMap, field.key);

          return (
            <MatrixFieldShell key={field.id} $placementCSS={placementCSS} $isVirtual={false}>
              {useViewMode ? (
                <UdfFieldViewControl field={field} name={name} control={control} viewPresentation={viewPresentation} />
              ) : (
                <UdfFieldControl field={field} name={name} control={control} disabled={isReadOnly} />
              )}
            </MatrixFieldShell>
          );
        })}
      </MatrixFieldsRoot>
    </UdfSectionRoot>
  );
});
